// ─────────────────────────────────────────────────────────────────────────────
// WP-F4-b · 6.3 — Las dos tablas de la maqueta de Notificaciones.
//
// 🔴 Las filas las declara el BACKEND, no esta pantalla. El plan afirmaba que las dos tablas «se
// derivan del catálogo filtrando por audiencia» y al medirlo era falso: filtrar por `Employee` da 19
// tipos y la tabla Shine de la maqueta tiene 5 filas, y además las dos tablas MEZCLAN audiencias —
// «Reco enviado a Brillamos» y «Mensaje reportado al emisor» son los dos de audiencia `Sender`—.
//
// Y al cruzar las 13 filas de la maqueta contra lo que el sistema emite salen NUEVE reales. Las
// cuatro que faltan están declaradas con su motivo en el servidor: dos no existen como aviso, una es
// la misma fila que otra vista desde el destinatario y la cuarta no encaja con ningún tipo. Pintarlas
// a mano aquí habría devuelto las cuatro decorativas — y una fila nueva no aparecería nunca.
// ─────────────────────────────────────────────────────────────────────────────
import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { NOTIFICATION_ROW_CHANNELS, useSaveRecognitionRowSubject } from '@/api/recognition';
import type { NotificationRowChannel, NotificationRowDto } from '@/api/recognition';

/** Los siete idiomas del módulo, en el orden en que se editan. */
const LANGUAGES = ['es', 'en', 'de', 'pt', 'nl', 'fr', 'ro'] as const;

const CHANNEL_ICON: Record<NotificationRowChannel, string> = {
  inapp: 'fa-bell',
  email: 'fa-envelope',
  teams: 'fa-comment-dots',
};

interface Props {
  table: 'shine' | 'admin';
  rows: NotificationRowDto[];
  /** Estado local de los interruptores, por clave `plantilla:canal`. `null` = heredado. */
  overrides: Record<string, boolean | null>;
  onToggle: (key: string, value: boolean | null) => void;
  disabled?: boolean;
}

export function NotificationRowsTable({ table, rows, overrides, onToggle, disabled }: Props) {
  const { t } = useTranslation('recognition');
  const [editando, setEditando] = useState<NotificationRowDto | null>(null);

  const propias = useMemo(() => rows.filter((row) => row.table === table), [rows, table]);

  if (propias.length === 0) return null;

  return (
    <div className="adm-notif-group">
      <div className="adm-notif-group-head">
        <div className="adm-notif-group-title-row">
          <span className="adm-notif-group-dot adm-notif-group-dot--cats" />
          <h3 className="adm-notif-group-title">{t(`admin.notifications.tables.${table}`)}</h3>
          <span className="adm-notif-group-count">{propias.length}</span>
        </div>
      </div>

      <div className="adm-notif-rows-wrap">
        <table className="adm-notif-rows">
          <thead>
            <tr>
              <th scope="col">{t('admin.notifications.tables.colType')}</th>
              <th scope="col">{t('admin.notifications.tables.colSubject')}</th>
              {NOTIFICATION_ROW_CHANNELS.map((canal) => (
                <th key={canal} scope="col" className="adm-notif-rows-ch">
                  <i className={`fas ${CHANNEL_ICON[canal]}`} aria-hidden="true" />{' '}
                  <span className="adm-notif-rows-ch-label">
                    {t(`admin.notifications.channel.${canal}`)}
                  </span>
                </th>
              ))}
              <th scope="col">{t('admin.notifications.tables.colLanguages')}</th>
            </tr>
          </thead>
          <tbody>
            {propias.map((row) => (
              <tr key={row.key}>
                <td>
                  <strong className="adm-notif-rows-name">
                    {t(`admin.notifications.tables.rows.${row.key}.name`)}
                  </strong>
                  <span className="adm-notif-rows-trigger">
                    {t(`admin.notifications.tables.rows.${row.key}.trigger`)}
                  </span>
                </td>
                <td>
                  {/* Solo lectura aquí: el asunto se edita en el modal, que es donde están los siete
                      idiomas. Un input editable en la tabla dejaría cambiar el español y perder de
                      vista que hay otros seis textos que siguen diciendo lo anterior. */}
                  <span className="adm-notif-rows-subject">{row.subjects.es ?? '—'}</span>
                </td>
                {NOTIFICATION_ROW_CHANNELS.map((canal) => {
                  const clave = `${row.key}:${canal}`;
                  const guardado = row.channels[canal] ?? null;
                  const actual = clave in overrides ? overrides[clave] : guardado;
                  return (
                    <td key={canal} className="adm-notif-rows-ch">
                      <label className="adm-notif-rows-switch">
                        <input
                          type="checkbox"
                          role="switch"
                          disabled={disabled}
                          aria-label={`${t(`admin.notifications.tables.rows.${row.key}.name`)} · ${t(`admin.notifications.channel.${canal}`)}`}
                          // `null` es HEREDADO y se pinta encendido: la fila hereda «sí» mientras
                          // nadie diga lo contrario, que es lo que hace el servidor.
                          checked={actual !== false}
                          onChange={(e) => onToggle(clave, e.target.checked ? null : false)}
                        />
                        <span className="slider" />
                      </label>
                    </td>
                  );
                })}
                <td>
                  <button
                    type="button"
                    className="adm-notif-rows-edit"
                    onClick={() => setEditando(row)}
                    aria-label={t('admin.notifications.tables.editSubject', {
                      row: t(`admin.notifications.tables.rows.${row.key}.name`),
                    })}
                  >
                    <i className="fas fa-pen" aria-hidden="true" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editando && (
        <SubjectModal row={editando} onClose={() => setEditando(null)} />
      )}
    </div>
  );
}

/**
 * El modal del lápiz: el asunto en los SIETE idiomas.
 *
 * Los siete llegan con la fila, no se piden aquí: pedirlos al abrir obligaría a una llamada por fila
 * y dejaría el modal en blanco el primer instante.
 */
function SubjectModal({ row, onClose }: { row: NotificationRowDto; onClose: () => void }) {
  const { t } = useTranslation('recognition');
  const save = useSaveRecognitionRowSubject();
  const [valores, setValores] = useState<Record<string, string>>({});

  useEffect(() => {
    setValores(Object.fromEntries(LANGUAGES.map((lang) => [lang, row.subjects[lang] ?? ''])));
  }, [row]);

  const sucio = LANGUAGES.some((lang) => (valores[lang] ?? '') !== (row.subjects[lang] ?? ''));

  const guardar = async () => {
    // Solo viajan los idiomas que cambiaron: el servidor no toca lo que no le mandan, y reenviar los
    // siete cada vez reescribiría plantillas que nadie tocó.
    const cambiados = Object.fromEntries(
      LANGUAGES
        .filter((lang) => (valores[lang] ?? '') !== (row.subjects[lang] ?? ''))
        .map((lang) => [lang, valores[lang] ?? '']),
    );
    await save.mutateAsync({ templateKey: row.key, subjects: cambiados });
    onClose();
  };

  // createPortal a document.body: `.plataforma-main` (recognition-shell.css) lleva `isolation:
  // isolate`, que aísla el z-index de `.modal-overlay` dentro de su propio contexto de apilamiento
  // — nunca compite contra el z-index explícito del Sidebar, que queda pintado encima. Mismo fix ya
  // aplicado en BulkUploadModal.tsx/WinnersImportWizardModal.tsx.
  return createPortal((
    <div className="modal-overlay" role="presentation" onClick={onClose}>
      <div
        className="adm-notif-modal"
        role="dialog"
        aria-modal="true"
        aria-label={t('admin.notifications.tables.subjectByLanguage')}
        onClick={(e) => e.stopPropagation()}
      >
        <h4 className="adm-notif-modal-title">
          {t(`admin.notifications.tables.rows.${row.key}.name`)}
        </h4>
        <p className="adm-notif-item-desc">{t('admin.notifications.tables.subjectByLanguage')}</p>

        <div className="adm-notif-modal-langs">
          {LANGUAGES.map((lang) => (
            <label key={lang} className="adm-notif-modal-lang">
              <span className="adm-notif-modal-lang-code">{lang.toUpperCase()}</span>
              <input
                type="text"
                className="adm-notif-modal-input"
                value={valores[lang] ?? ''}
                onChange={(e) => setValores((prev) => ({ ...prev, [lang]: e.target.value }))}
              />
            </label>
          ))}
        </div>

        {save.isError && (
          <p className="adm-notif-state" role="alert">{t('admin.notifications.saveError')}</p>
        )}

        <div className="adm-notif-modal-foot">
          <button type="button" className="btn btn--secondary" onClick={onClose}>
            {t('admin.notifications.tables.close')}
          </button>
          <button
            type="button"
            className="btn btn--primary"
            onClick={guardar}
            disabled={!sucio || save.isPending}
          >
            {save.isPending ? t('admin.saving') : t('admin.notifications.tables.saveSubject')}
          </button>
        </div>
      </div>
    </div>
  ), document.body);
}

export default NotificationRowsTable;
